// The EESA-R ceiling (Decision Box) rule, as written in the EESAPP Guide,
// 2nd ed. (p.30-33), kept in one place so the screens and the wording agree.
//
// Groups 1, 2 and 3 each have a Decision Box: FIVE CONSECUTIVE 0 scores stop
// that group. What that zeros differs by group:
//   Group 1 ceiling -> remaining items in Group 1 and all of Groups 2-5.
//   Group 2 ceiling -> remaining items in Group 2 and all of Groups 3-5.
//   Group 3 ceiling -> remaining items in Group 3 only. "Then administer
//                      Groups 4 and 5."
// Groups 4 and 5 have no Decision Box: "give all required number of items".

export const CEILING_RUN_LENGTH = 5;

// Groups that have a Decision Box at all.
export const CEILING_GROUPS = [1, 2, 3];

// Groups whose ceiling also zeros the groups after it.
export const CARRY_FORWARD_CEILING_GROUPS = [1, 2];

/**
 * Longest run of consecutive 0-scored items. Items with no score yet (and
 * flagged items waiting on a score) break the run, which is what "consecutive"
 * means on the test form.
 * @param {Array} items attempt items, in test order
 */
export function longestZeroRun(items = []) {
    let longest = 0;
    let run = 0;

    for (const item of items || []) {
        const scored = item?.score !== null && item?.score !== undefined && item?.score !== '';

        if (scored && Number(item.score) === 0) {
            run += 1;
            if (run > longest) longest = run;
        } else {
            run = 0;
        }
    }

    return longest;
}

/**
 * Has this group hit its ceiling? Only Groups 1-3 can.
 */
export function hasCeiling(groupId, items = []) {
    if (!CEILING_GROUPS.includes(Number(groupId))) return false;

    return longestZeroRun(items) >= CEILING_RUN_LENGTH;
}

/**
 * Per-group longest zero run, from the assessment result's items map.
 * @param {Object} itemsByGroup {1: [...], ... 5: [...]}
 */
export function zeroRunsByGroup(itemsByGroup = {}) {
    const runs = {};
    for (let i = 1; i <= 5; i++) {
        runs[i] = longestZeroRun(itemsByGroup?.[i] || []);
    }
    return runs;
}

/**
 * The earlier group whose ceiling closes this group, or null if this group is
 * still to be given. A Group 3 ceiling never closes Groups 4 and 5.
 * @param {Number} groupId the group being opened
 * @param {Object} itemsByGroup {1: [...], ... 5: [...]}
 */
export function blockingCeilingGroup(groupId, itemsByGroup = {}) {
    const current = Number(groupId);
    if (!current || current <= 1) return null;

    for (const g of CARRY_FORWARD_CEILING_GROUPS) {
        if (g >= current) break;
        if (hasCeiling(g, itemsByGroup?.[g] || [])) return g;
    }

    return null;
}

/**
 * What a ceiling in this group zeros, in words. Used on the blocked-group
 * screen and in the automatic-scoring dialog so they cannot drift apart.
 */
export function ceilingScopeText(ceilingGroup) {
    const g = Number(ceilingGroup);

    if (g === 1 || g === 2) {
        return `The remaining items in Group ${g} and all items in Groups ${g + 1}-5 are scored 0.`;
    }

    if (g === 3) {
        return 'The remaining items in Group 3 are scored 0. Groups 4 and 5 are still given - a Group 3 ceiling does not end the test.';
    }

    return '';
}

export const CEILING_RULE_NOTE =
    'Ceiling: five consecutive 0 scores in Groups 1, 2 or 3 stop that group. Groups 4 and 5 have no ceiling.';

export const CEILING_RULE_REF = 'EESAPP Guide, 2nd ed., p.30-33';
